import React, { useEffect } from "react";
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  RadioGroup,
  Radio,
  FormControlLabel,
  Button,
  Typography,
  Slider,
  IconButton,
  Paper,
  ListItemText,
  Checkbox,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { Formik, FieldArray } from "formik";
import * as Yup from "yup";
import { useSaveAnswers, useAnswers } from "apiRequests/useAnswers";
import StepNav from "@/components/StepNav";

// Official College Board AP course catalog.
const AP_SUBJECTS = [
  "AP Art History",
  "AP Art and Design: 2-D",
  "AP Art and Design: 3-D",
  "AP Art and Design: Drawing",
  "AP Biology",
  "AP Calculus AB",
  "AP Calculus BC",
  "AP Chemistry",
  "AP Chinese Language and Culture",
  "AP Comparative Government and Politics",
  "AP Computer Science A",
  "AP Computer Science Principles",
  "AP English Language and Composition",
  "AP English Literature and Composition",
  "AP Environmental Science",
  "AP European History",
  "AP French Language and Culture",
  "AP German Language and Culture",
  "AP Human Geography",
  "AP Italian Language and Culture",
  "AP Japanese Language and Culture",
  "AP Latin",
  "AP Macroeconomics",
  "AP Microeconomics",
  "AP Music Theory",
  "AP Physics 1: Algebra-Based",
  "AP Physics 2: Algebra-Based",
  "AP Physics C: Electricity and Magnetism",
  "AP Physics C: Mechanics",
  "AP Precalculus",
  "AP Psychology",
  "AP Research",
  "AP Seminar",
  "AP Spanish Language and Culture",
  "AP Spanish Literature and Culture",
  "AP Statistics",
  "AP United States Government and Politics",
  "AP United States History",
  "AP World History: Modern",
];

const AP_SCORES = [1, 2, 3, 4, 5];

export default function AcademicDetailsForm() {
  const { data, error, loading, refetch, isReady } = useAnswers();
  const saveAnswers = useSaveAnswers();

  return (
    <div>
      {!isReady ? <p>Loading...</p>:(
        <Formik
          initialValues={{
            name: data?.academics?.name || "",
            state: data?.academics?.state || "",
            city: data?.academics?.city || "",
            grade: data?.academics?.grade || "",
            graduationYear: data?.academics?.graduationYear || "",
            gpa: data?.academics?.gpa || "",
            classRank: data?.academics?.classRank || "",
            testType: data?.academics?.testType || "SAT",
            highestVerbal: data?.academics?.highestVerbal || "",
            highestComposite: data?.academics?.highestComposite || "",
            highestMath: data?.academics?.highestMath || "",
            apExams: data?.academics?.apExams as { subject: string; score: string }[] || [] as { subject: string; score: string }[],
            difficulty: data?.academics?.difficulty || 1,
          }}
          validationSchema={Yup.object().shape({
            name: Yup.string().max(255).required("Name is required"),
            gpa: Yup.number().min(0).max(5).nullable(),
          })}
          onSubmit={(values) => {
            // Save only; StepNav handles advancing to the next step.
            return saveAnswers({
              academics: values
            }).then((results)=>{
              refetch()
              console.log("Answers saved", results)
            })
          }}
        >
          {({ values, handleChange, setFieldValue, handleSubmit, submitForm, validateForm }) => (
            <form onSubmit={handleSubmit}>
              <Box p={3} display="flex" flexDirection="column" gap={3}>
                {/* Academic Details */}
                <Paper elevation={3} sx={{ p: 3 }}>
                  <Typography variant="h6" mb={2}>
                    High School Details
                  </Typography>
                  <Grid container spacing={2} rowSpacing={5}>
                    <Grid item xs={12} md={3}>
                      <TextField
                        name="name"
                        label="Name"
                        fullWidth
                        value={values.name}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth>
                        <InputLabel>State</InputLabel>
                        <Select
                          name="state"
                          value={values.state}
                          onChange={handleChange}
                        >
                          <MenuItem value="">Select</MenuItem>
                          <MenuItem value="AL">Alabama</MenuItem>
                          <MenuItem value="AK">Alaska</MenuItem>
                          <MenuItem value="AZ">Arizona</MenuItem>
                          <MenuItem value="AR">Arkansas</MenuItem>
                          <MenuItem value="CA">California</MenuItem>
                          <MenuItem value="CO">Colorado</MenuItem>
                          <MenuItem value="CT">Connecticut</MenuItem>
                          <MenuItem value="DE">Delaware</MenuItem>
                          <MenuItem value="FL">Florida</MenuItem>
                          <MenuItem value="GA">Georgia</MenuItem>
                          <MenuItem value="HI">Hawaii</MenuItem>
                          <MenuItem value="ID">Idaho</MenuItem>
                          <MenuItem value="IL">Illinois</MenuItem>
                          <MenuItem value="IN">Indiana</MenuItem>
                          <MenuItem value="IA">Iowa</MenuItem>
                          <MenuItem value="KS">Kansas</MenuItem>
                          <MenuItem value="KY">Kentucky</MenuItem>
                          <MenuItem value="LA">Louisiana</MenuItem>
                          <MenuItem value="ME">Maine</MenuItem>
                          <MenuItem value="MD">Maryland</MenuItem>
                          <MenuItem value="MA">Massachusetts</MenuItem>
                          <MenuItem value="MI">Michigan</MenuItem>
                          <MenuItem value="MN">Minnesota</MenuItem>
                          <MenuItem value="MS">Mississippi</MenuItem>
                          <MenuItem value="MO">Missouri</MenuItem>
                          <MenuItem value="MT">Montana</MenuItem>
                          <MenuItem value="NE">Nebraska</MenuItem>
                          <MenuItem value="NV">Nevada</MenuItem>
                          <MenuItem value="NH">New Hampshire</MenuItem>
                          <MenuItem value="NJ">New Jersey</MenuItem>
                          <MenuItem value="NM">New Mexico</MenuItem>
                          <MenuItem value="NY">New York</MenuItem>
                          <MenuItem value="NC">North Carolina</MenuItem>
                          <MenuItem value="ND">North Dakota</MenuItem>
                          <MenuItem value="OH">Ohio</MenuItem>
                          <MenuItem value="OK">Oklahoma</MenuItem>
                          <MenuItem value="OR">Oregon</MenuItem>
                          <MenuItem value="PA">Pennsylvania</MenuItem>
                          <MenuItem value="RI">Rhode Island</MenuItem>
                          <MenuItem value="SC">South Carolina</MenuItem>
                          <MenuItem value="SD">South Dakota</MenuItem>
                          <MenuItem value="TN">Tennessee</MenuItem>
                          <MenuItem value="TX">Texas</MenuItem>
                          <MenuItem value="UT">Utah</MenuItem>
                          <MenuItem value="VT">Vermont</MenuItem>
                          <MenuItem value="VA">Virginia</MenuItem>
                          <MenuItem value="WA">Washington</MenuItem>
                          <MenuItem value="WV">West Virginia</MenuItem>
                          <MenuItem value="WI">Wisconsin</MenuItem>
                          <MenuItem value="WY">Wyoming</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        name="city"
                        label="City"
                        fullWidth
                        value={values.city}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        name="grade"
                        label="Grade"
                        fullWidth
                        value={values.grade}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        name="graduationYear"
                        label="Graduation Year"
                        type="date"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={values.graduationYear}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        name="gpa"
                        label="Unweighted GPA"
                        fullWidth
                        value={values.gpa}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth>
                        <InputLabel>Class Rank</InputLabel>
                        <Select
                          name="classRank"
                          value={values.classRank}
                          onChange={handleChange}
                        >
                          <MenuItem value="">Select</MenuItem>
                          <MenuItem value="top1">Top 1%</MenuItem>
                          <MenuItem value="top5">Top 5%</MenuItem>
                          <MenuItem value="top10">Top 10%</MenuItem>
                          <MenuItem value="top25">Top 25%</MenuItem>
                          <MenuItem value="top50">Top 50%</MenuItem>
                          <MenuItem value="below50">Below 50%</MenuItem>
                          <MenuItem value="na">N/A (school does not rank)</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Paper>

                {/* Tests */}
                <Paper elevation={3} sx={{ p: 3 }}>
                  <Typography variant="h6" mb={2}>
                    Tests
                  </Typography>
                  <RadioGroup
                    row
                    name="testType"
                    value={values.testType}
                    onChange={handleChange}
                  >
                    <FormControlLabel value="SAT" control={<Radio />} label="SAT" />
                    <FormControlLabel value="ACT" control={<Radio />} label="ACT" />
                  </RadioGroup>
                  {values.testType === "SAT" ? (
                    <Grid container spacing={2} rowSpacing={5} mt={1}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          name="highestVerbal"
                          label="Highest Verbal Score"
                          fullWidth
                          value={values.highestVerbal}
                          onChange={handleChange}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          name="highestMath"
                          label="Highest Math Score"
                          fullWidth
                          value={values.highestMath}
                          onChange={handleChange}
                        />
                      </Grid>
                    </Grid>
                  ) : (
                    <Grid container spacing={2} rowSpacing={5} mt={1}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          name="highestComposite"
                          label="Highest Composite Score"
                          fullWidth
                          value={values.highestComposite}
                          onChange={handleChange}
                        />
                      </Grid>
                    </Grid>
                  )}
                </Paper>

                {/* AP Exams */}
                <Paper elevation={3} sx={{ p: 3 }}>
                  <Typography variant="h6" mb={2}>
                    AP Exams
                  </Typography>
                  <FieldArray name="apExams">
                    {({ push, remove }) => (
                      <>
                        {values.apExams.length === 0 ? (
                          <Typography variant="body2" color="text.secondary" mb={2}>
                            No AP exams added.
                          </Typography>
                        ) : (
                          values.apExams.map((exam, index) => (
                            <Box
                              key={index}
                              display="flex"
                              alignItems="center"
                              gap={2}
                              mb={2}
                            >
                              <TextField
                                select
                                name={`apExams.${index}.subject`}
                                label="Subject"
                                fullWidth
                                value={exam.subject}
                                onChange={handleChange}
                              >
                                {AP_SUBJECTS.map((s) => (
                                  <MenuItem key={s} value={s}>
                                    {s}
                                  </MenuItem>
                                ))}
                              </TextField>
                              <TextField
                                select
                                name={`apExams.${index}.score`}
                                label="Score"
                                fullWidth
                                value={exam.score}
                                onChange={handleChange}
                                sx={{ minWidth: 120 }}
                              >
                                {AP_SCORES.map((n) => (
                                  <MenuItem key={n} value={n}>
                                    {n}
                                  </MenuItem>
                                ))}
                              </TextField>
                              <IconButton
                                color="error"
                                onClick={() => remove(index)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          ))
                        )}
                        <Button
                          startIcon={<AddIcon />}
                          onClick={() => push({ subject: "", score: "" })}
                          sx={{ mt: 1 }}
                        >
                          Add More
                        </Button>
                      </>
                    )}
                  </FieldArray>
                </Paper>

                {/* Curriculum Difficulty */}
                <Paper elevation={3} sx={{ p: 3 }}>
                  <Typography variant="h6" mb={1}>
                    Curriculum Difficulty
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    An important factor in gaining admission to the best and most
                    selective colleges is the rigor and difficulty level of your
                    high school curriculum. An example of the highest level of a
                    rigorous curriculum is someone who has taken multiple Advanced
                    Placement courses beginning in 11th grade, and received the
                    highest grades consistently, while maintaining multiple
                    extracurricular activities after school.
                  </Typography>
                  <Slider
                    name="difficulty"
                    value={values.difficulty}
                    onChange={(_, val) => setFieldValue("difficulty", val)}
                    step={1}
                    marks
                    min={1}
                    max={5}
                    valueLabelDisplay="on"
                  />
                </Paper>

                {/* Actions */}
                <StepNav
                  onSave={async () => {
                    const errors = await validateForm();
                    if (Object.keys(errors).length > 0) {
                      submitForm(); // surface validation errors
                      return false;
                    }
                    await submitForm();
                    return true;
                  }}
                />
              </Box>
            </form>
          )}
        </Formik>
      )}
    </div>
  );
}
